import * as React from "react"
import { useTracker } from "@modules/analytics"
import { Link, Spacer, ToggleCheckbox } from "gatsby-interface"
import { useToggleManualHostingDeploys } from "../hooks/useToggleManualHostingDeploys"
import { siteBuildsSettings as text } from "@modules/locales/default.js"
import { FormattedMessage } from "@modules/locales"
import { getSiteDetailsTabBasePath } from "@modules/site/details/utils"
import { SiteDetailsTab } from "@modules/site/details/constants"
import { getPathToUpgradeToProfessional } from "@modules/organization/shared/utils"
import { MdArrowForward, MdInfoOutline } from "react-icons/md"
import { useRollbacksAndManualDeploysAvailable } from "@modules/organization/shared/hooks/useRollbacksAndManualDeploysAvailable"

export type EnableAutomaticDeploysProps = {
  siteId: string
  organizationId: string
  manualHostingDeploysEnabled: boolean
  gatsbyHostingOn: boolean
}

export function EnableAutomaticDeploys({
  siteId,
  organizationId,
  manualHostingDeploysEnabled,
  gatsbyHostingOn,
}: EnableAutomaticDeploysProps) {
  const isEligiblePlan =
    useRollbacksAndManualDeploysAvailable(organizationId).result

  const linkToHostingSettings = `${getSiteDetailsTabBasePath(
    SiteDetailsTab.Settings,
    siteId,
    organizationId
  )}/hosting`

  const linkToPlanUpgrade = getPathToUpgradeToProfessional(organizationId)

  const [toggleManualHostingDeploys, { errorAlert }] =
    useToggleManualHostingDeploys(siteId)

  const { trackButtonClicked } = useTracker()

  if (gatsbyHostingOn === undefined) {
    return null
  }

  return (
    <React.Fragment>
      <ToggleCheckbox
        id="enable-automatic-deploys-checkbox"
        label={
          <React.Fragment>
            {text.labels.enableAutomaticDeploys}
            <Spacer direction="horizontal" size={3} />
            <Link
              href={`https://gatsby.dev/site-settings-enable-automatic-deploys-info`}
              title={text.labels.learnAboutAutomaticDeploys}
            >
              <MdInfoOutline />
            </Link>
          </React.Fragment>
        }
        hint={
          <React.Fragment>
            {text.messages.deploysAllSuccessfulProductionBuilds}{" "}
            {!isEligiblePlan && (
              <FormattedMessage<never, "link">
                message={text.messages.notCustomizableForYourPlan}
                tags={{
                  link: function (content) {
                    return (
                      <Link to={linkToPlanUpgrade} variant="SIMPLE">
                        {content} <MdArrowForward />
                      </Link>
                    )
                  },
                }}
              />
            )}
            {isEligiblePlan && !gatsbyHostingOn && (
              <FormattedMessage<never, "link">
                message={text.messages.onlyCustomizableIfHostingOn}
                tags={{
                  link: function (content) {
                    return (
                      <Link to={linkToHostingSettings} variant="SIMPLE">
                        {content}
                      </Link>
                    )
                  },
                }}
              />
            )}
          </React.Fragment>
        }
        checked={gatsbyHostingOn ? !manualHostingDeploysEnabled : true}
        onChange={() => {
          trackButtonClicked(`update automatic deploys`, {
            uiSource: `Site Settings`,
          })
          toggleManualHostingDeploys()
        }}
        disabled={!gatsbyHostingOn || !isEligiblePlan}
      />

      {errorAlert}
    </React.Fragment>
  )
}
